import type { BlockRegistry, FieldSchema, Node, ReportTemplate } from "@broadpaper/core";
import { indexTemplate, type RegionId } from "@broadpaper/core";
import type { DragState, DropTarget } from "../state/session.js";

/**
 * What the data panel puts on a drag.
 *
 * `bound` is `expr` with the field's own formatter appended — the value a prop
 * that renders text should use — kept beside the bare path because a collection
 * binding wants the path and nothing else.
 */
export interface FieldPayload {
  expr: string;
  bound?: string;
  label: string;
  type?: string;
  schema?: FieldSchema;
}

export interface DropContext {
  template: ReportTemplate;
  registry: BlockRegistry;
  /** The scrolling canvas element. */
  canvas: HTMLElement;
  /** The element overlays are positioned relative to. */
  overlayOrigin: HTMLElement;
}

interface ZoneInfo {
  el: HTMLElement;
  parentId: string | null;
  region: RegionId;
  parentType: string;
}

function regionOfElement(el: Element, first: boolean): RegionId {
  const region = el.closest<HTMLElement>("[data-region]")?.getAttribute("data-region") ?? "body";
  if (region === "header") return first ? "header:first" : "header";
  if (region === "footer") return first ? "footer:first" : "footer";
  return "body";
}

function dragType(drag: DragState): string {
  switch (drag.kind) {
    case "new":
      return String(drag.payload);
    case "row":
      return "row";
    case "library":
      return "section";
    case "field":
      return "field";
    case "move":
      return "";
  }
}

/**
 * Computes the drop target for the current pointer position. Pure with respect
 * to the editor state; reads the DOM for geometry only.
 */
export function computeDropTarget(x: number, y: number, drag: DragState, ctx: DropContext, pageFirst: (el: Element) => boolean): DropTarget | null {
  const doc = ctx.canvas.ownerDocument;
  const index = indexTemplate(ctx.template);
  const movingNode = drag.kind === "move" ? index.get(String(drag.payload))?.node : undefined;
  const type = movingNode ? movingNode.type : dragType(drag);
  if (drag.kind === "library") {
    const item = ctx.template.library?.find((l) => l.id === drag.payload);
    if (item) return sectionDrop(x, y, ctx, item.node.type === "section" ? "section" : item.node.type, pageFirst, index);
  }
  if (type === "section" || movingNode?.type === "section") return sectionDrop(x, y, ctx, "section", pageFirst, index, movingNode?.id);

  const stack = doc.elementsFromPoint(x, y);
  const originRect = ctx.overlayOrigin.getBoundingClientRect();
  const rel = (r: DOMRect) => ({ x: r.left - originRect.left, y: r.top - originRect.top, width: r.width, height: r.height });

  // Field drops onto text-like blocks insert a token.
  if (drag.kind === "field") {
    for (const el of stack) {
      const nodeEl = (el as HTMLElement).closest?.<HTMLElement>("[data-key][data-type='text'],[data-key][data-type='heading']");
      if (nodeEl && ctx.canvas.contains(nodeEl)) {
        const r = rel(nodeEl.getBoundingClientRect());
        return { parentId: null, region: "body", index: 0, line: null, zone: r, valid: true, textTargetId: nodeEl.getAttribute("data-node")! };
      }
      break;
    }
  }

  // Deepest dropzone under the pointer that accepts the dragged type.
  let zone: ZoneInfo | null = null;
  for (const el of stack) {
    const z = (el as HTMLElement).closest?.<HTMLElement>("[data-dropzone]");
    if (!z || !ctx.canvas.contains(z)) continue;
    let cur: HTMLElement | null = z;
    while (cur) {
      const parentId = cur.getAttribute("data-dropzone")!;
      const parentNode = index.get(parentId)?.node;
      const parentType = parentNode?.type ?? "section";
      const accepts = drag.kind === "field" ? ctx.registry.accepts(parentType, "field") : ctx.registry.accepts(parentType, type);
      const insideSelf = movingNode ? parentId === movingNode.id || isInside(index, movingNode.id, parentId) : false;
      if (accepts && !insideSelf && !parentNode?.locked) {
        zone = { el: cur, parentId, region: regionOfElement(cur, pageFirst(cur)), parentType };
        break;
      }
      cur = cur.parentElement?.closest<HTMLElement>("[data-dropzone]") ?? null;
    }
    if (zone) break;
  }

  if (!zone) {
    // Over a page body but not over any dropzone — the margin beside a section,
    // or the gap between two. Falling back to the last section on the page put
    // blocks a long way from where they were dropped, so pick the section the
    // pointer is actually nearest to instead.
    const pageBody = stack.find((el) => (el as HTMLElement).classList?.contains("bp-page__body")) as HTMLElement | undefined;
    if (!pageBody) return null;
    const region = regionOfElement(pageBody, pageFirst(pageBody));
    const sections = [...pageBody.querySelectorAll<HTMLElement>(".bp-section [data-dropzone]")].filter((el) => el.closest(".bp-section")?.parentElement?.closest(".bp-section") === null);
    let best: HTMLElement | undefined;
    let bestDistance = Infinity;
    for (const el of sections) {
      const r = el.getBoundingClientRect();
      const distance = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
      if (distance < bestDistance) {
        best = el;
        bestDistance = distance;
        if (distance === 0) break;
      }
    }
    if (!best) return null;
    zone = { el: best, parentId: best.getAttribute("data-dropzone"), region, parentType: "section" };
  }

  const children = [...zone.el.children].filter((c) => c.matches("[data-node]")) as HTMLElement[];
  const rects = children.map((c) => c.getBoundingClientRect());
  let insertIndex = children.length;
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i]!;
    if (y < r.top + r.height / 2) {
      insertIndex = i;
      break;
    }
  }
  // Map DOM index to model index (design mode renders the same children in order).
  const parentNode = zone.parentId ? index.get(zone.parentId)?.node : undefined;
  const modelChildren = parentNode?.children ?? [];
  const domIds = children.map((c) => c.getAttribute("data-node"));
  let modelIndex = modelChildren.length;
  if (insertIndex < children.length) {
    const targetId = domIds[insertIndex];
    const mi = modelChildren.findIndex((n) => n.id === targetId);
    modelIndex = mi >= 0 ? mi : modelChildren.length;
  }
  const zr = rel(zone.el.getBoundingClientRect());
  let lineY: number;
  if (rects.length === 0) lineY = zr.y + zr.height / 2;
  else if (insertIndex < rects.length) lineY = rel(rects[insertIndex]!).y - 3;
  else lineY = rel(rects[rects.length - 1]!).y + rects[rects.length - 1]!.height + 3;

  if (movingNode) {
    const entry = index.get(movingNode.id)!;
    const sameParent = (entry.parent?.id ?? null) === zone.parentId;
    if (sameParent && (modelIndex === entry.index || modelIndex === entry.index + 1)) {
      return { parentId: zone.parentId, region: zone.region, index: modelIndex, line: null, zone: null, valid: false };
    }
  }
  return { parentId: zone.parentId, region: zone.region, index: modelIndex, line: { x: zr.x + 2, y: lineY, width: Math.max(0, zr.width - 4) }, zone: rects.length === 0 ? zr : null, valid: true };
}

function isInside(index: ReturnType<typeof indexTemplate>, ancestorId: string, id: string): boolean {
  let cur = index.get(id);
  while (cur?.parent) {
    if (cur.parent.id === ancestorId) return true;
    cur = index.get(cur.parent.id);
  }
  return false;
}

/** Sections drop between other sections in the region under the pointer. */
function sectionDrop(x: number, y: number, ctx: DropContext, _type: string, pageFirst: (el: Element) => boolean, index: ReturnType<typeof indexTemplate>, movingId?: string): DropTarget | null {
  const doc = ctx.canvas.ownerDocument;
  const stack = doc.elementsFromPoint(x, y);
  const pageBody = stack.find((el) => (el as HTMLElement).classList?.contains("bp-page__body") || (el as HTMLElement).classList?.contains("bp-page__region")) as HTMLElement | undefined;
  if (!pageBody) return null;
  const region = regionOfElement(pageBody, pageFirst(pageBody));
  const originRect = ctx.overlayOrigin.getBoundingClientRect();
  // Top-level section elements visible on this page (one per fragment).
  const sectionEls = [...pageBody.querySelectorAll<HTMLElement>(".bp-section")].filter((el) => !el.parentElement?.closest(".bp-section"));
  const list = region === "body" ? ctx.template.body : region.startsWith("header") ? (region === "header" ? ctx.template.header.default : ctx.template.header.firstPage ?? []) : region === "footer" ? ctx.template.footer.default : ctx.template.footer.firstPage ?? [];
  let modelIndex = list.length;
  let lineY = 0;
  const bodyRect = pageBody.getBoundingClientRect();
  if (sectionEls.length === 0) {
    lineY = bodyRect.top - originRect.top + 8;
    modelIndex = 0;
  } else {
    let placed = false;
    for (const el of sectionEls) {
      const r = el.getBoundingClientRect();
      const frag = el.closest<HTMLElement>(".bp-fragment");
      const clipTop = frag ? frag.getBoundingClientRect().top : r.top;
      const visibleTop = Math.max(r.top, clipTop);
      const visibleBottom = frag ? Math.min(r.bottom, frag.getBoundingClientRect().bottom) : r.bottom;
      if (y < (visibleTop + visibleBottom) / 2) {
        const id = el.getAttribute("data-node")!;
        modelIndex = Math.max(0, list.findIndex((n) => n.id === id));
        lineY = visibleTop - originRect.top - 2;
        placed = true;
        break;
      }
    }
    if (!placed) {
      const lastEl = sectionEls[sectionEls.length - 1]!;
      const frag = lastEl.closest<HTMLElement>(".bp-fragment");
      const bottom = frag ? Math.min(lastEl.getBoundingClientRect().bottom, frag.getBoundingClientRect().bottom) : lastEl.getBoundingClientRect().bottom;
      const id = lastEl.getAttribute("data-node")!;
      const li = list.findIndex((n) => n.id === id);
      modelIndex = li >= 0 ? li + 1 : list.length;
      lineY = bottom - originRect.top + 2;
    }
  }
  if (movingId) {
    const entry = index.get(movingId);
    if (entry && entry.region === region && (modelIndex === entry.index || modelIndex === entry.index + 1)) return { parentId: null, region, index: modelIndex, line: null, zone: null, valid: false };
  }
  return { parentId: null, region, index: modelIndex, line: { x: bodyRect.left - originRect.left, y: lineY, width: bodyRect.width }, zone: null, valid: true };
}

export function nodeForDrop(drag: DragState, ctx: { registry: BlockRegistry; template: ReportTemplate; createNode: (type: string, props?: Record<string, unknown>) => Node; createRow: (layout: number[]) => Node; cloneLibrary: (id: string) => Node | undefined }): Node | undefined {
  switch (drag.kind) {
    case "new":
      return ctx.createNode(String(drag.payload));
    case "row":
      return ctx.createRow(drag.payload as number[]);
    case "library":
      return ctx.cloneLibrary(String(drag.payload));
    case "field": {
      const p = drag.payload as FieldPayload;
      const type = p.type === "array" ? "table" : "field";
      // The block decides what a dropped field becomes — a list gives a finished
      // table with a column per item field, a scalar gives a labelled value — so
      // a custom block can answer for itself rather than being fitted into a rule
      // written here. The fallbacks are what the designer did before the hook
      // existed, for a registry whose blocks predate it.
      const props = ctx.registry.get(type)?.fromField?.({ expr: p.expr, formatted: p.bound ?? p.expr, schema: p.schema ?? { type: (p.type ?? "string") as FieldSchema["type"] }, label: p.label });
      if (props) return ctx.createNode(type, props as Record<string, unknown>);
      if (type === "table") return ctx.createNode("table", { source: p.expr });
      return ctx.createNode("field", { label: p.label.split(" › ").pop() ?? p.label, value: p.bound ?? p.expr });
    }
    default:
      return undefined;
  }
}

/**
 * Where a click in the canvas would put a new block.
 *
 * This is the drop machinery asked a hypothetical question, so click-to-place
 * and drag-to-place can never disagree about what a position means. The type
 * used for the enquiry is a plain text block: it is accepted anywhere an
 * ordinary block is, which is what makes the answer a fair general one.
 */
export function computeInsertion(x: number, y: number, ctx: DropContext, pageFirst: (el: Element) => boolean): DropTarget | null {
  const target = computeDropTarget(x, y, { kind: "new", payload: "text", label: "Text", x, y }, ctx, pageFirst);
  return target && target.valid && target.line && !target.textTargetId ? target : null;
}
